import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as supportApi from '../Api/supportApi';

const SUPPORT_KEY = 'support';
const ACTIVE_THREAD_POLL_MS = 3000;
const INBOX_LIST_POLL_MS = 8000;

const pollingQueryOptions = (poll, intervalMs) => ({
	staleTime: 30 * 1000,
	refetchInterval: poll ? intervalMs : false,
	refetchIntervalInBackground: false,
	refetchOnWindowFocus: !poll,
});

export const useMySupportTickets = (
	enabled = true,
	{ poll = false, pollInterval = INBOX_LIST_POLL_MS } = {},
) =>
	useQuery({
		queryKey: [SUPPORT_KEY, 'mine'],
		queryFn: () => supportApi.getMySupportTickets().then((res) => res.data),
		enabled,
		...pollingQueryOptions(poll, pollInterval),
	});

export const useAdminSupportTickets = (
	params = {},
	enabled = true,
	{ poll = false, pollInterval = INBOX_LIST_POLL_MS } = {},
) =>
	useQuery({
		queryKey: [SUPPORT_KEY, 'admin', params],
		queryFn: () =>
			supportApi.getAdminSupportTickets(params).then((res) => res.data),
		enabled,
		...pollingQueryOptions(poll, pollInterval),
	});

export const useSupportTicket = (id, enabled = true, { poll = false } = {}) =>
	useQuery({
		queryKey: [SUPPORT_KEY, 'ticket', id],
		queryFn: () => supportApi.getSupportTicket(id).then((res) => res.data.ticket),
		enabled: !!id && enabled,
		...pollingQueryOptions(poll, ACTIVE_THREAD_POLL_MS),
	});

const syncTicketCaches = (queryClient, ticket) => {
	if (!ticket?._id) return;

	queryClient.setQueryData([SUPPORT_KEY, 'ticket', ticket._id], ticket);

	queryClient.setQueryData([SUPPORT_KEY, 'mine'], (existing) => {
		const base = Array.isArray(existing)
			? { tickets: existing, totalUnread: 0 }
			: existing;
		if (!base?.tickets) return base;
		const idx = base.tickets.findIndex((t) => t._id === ticket._id);
		let tickets;
		if (idx === -1) {
			tickets = [ticket, ...base.tickets];
		} else {
			tickets = [...base.tickets];
			tickets[idx] = { ...tickets[idx], ...ticket };
		}
		tickets.sort(
			(a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0),
		);
		const totalUnread = tickets.reduce((sum, t) => sum + (t.unreadCount || 0), 0);
		return { ...base, tickets, totalUnread };
	});

	queryClient.setQueriesData({ queryKey: [SUPPORT_KEY, 'admin'] }, (existing) => {
		if (!existing?.tickets) return existing;
		const idx = existing.tickets.findIndex((t) => t._id === ticket._id);
		if (idx === -1) {
			return {
				...existing,
				tickets: [ticket, ...existing.tickets],
				total: (existing.total || 0) + 1,
			};
		}
		const tickets = [...existing.tickets];
		tickets[idx] = { ...tickets[idx], ...ticket };
		tickets.sort(
			(a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0),
		);
		return { ...existing, tickets };
	});
};

export const useCreateSupportTicket = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data) => supportApi.createSupportTicket(data).then((res) => res.data),
		onSuccess: (data) => {
			syncTicketCaches(queryClient, data?.ticket);
		},
	});
};

export const useAddSupportMessage = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, ...data }) =>
			supportApi.addSupportTicketMessage(id, data).then((res) => res.data),
		onSuccess: (data) => {
			syncTicketCaches(queryClient, data?.ticket);
		},
	});
};

export const useUpdateSupportTicket = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, ...data }) =>
			supportApi.updateSupportTicket(id, data).then((res) => res.data),
		onSuccess: (data) => {
			syncTicketCaches(queryClient, data?.ticket);
		},
	});
};
